import React, { createContext, useContext, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

export interface NavigationTheme {
  // Colors
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  hoverColor: string;
  activeColor: string;
  backgroundColor: string;
  borderColor: string;
  
  // Typography
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  lineHeight: string;
  
  // Spacing
  padding: string;
  margin: string;
  gap: string;
  
  // Effects
  borderRadius: string;
  shadow: string;
  transition: string;
  
  // States
  hover: {
    backgroundColor: string;
    textColor: string;
    transform: string;
  };
  
  active: {
    backgroundColor: string;
    textColor: string;
    borderColor: string;
  };
  
  // Variants
  variants: {
    horizontal: Record<string, string>;
    vertical: Record<string, string>;
    mobile: Record<string, string>;
    dropdown: Record<string, string>;
  };
}

interface NavigationThemeContextType {
  theme: NavigationTheme;
  updateTheme: (updates: Partial<NavigationTheme>) => void;
  resetTheme: () => void;
  getThemeClasses: (variant: string, state?: string) => string;
}

const NavigationThemeContext = createContext<NavigationThemeContextType | undefined>(undefined);

export const useNavigationTheme = () => {
  const context = useContext(NavigationThemeContext);
  if (!context) {
    throw new Error('useNavigationTheme must be used within NavigationThemeProvider');
  }
  return context;
};

const DEFAULT_THEME: NavigationTheme = {
  // Colors - using CSS custom properties for theme consistency
  primaryColor: 'hsl(var(--primary))',
  secondaryColor: 'hsl(var(--secondary))',
  textColor: 'hsl(var(--foreground))',
  hoverColor: 'hsl(var(--primary) / 0.1)',
  activeColor: 'hsl(var(--primary))',
  backgroundColor: 'hsl(var(--background))',
  borderColor: 'hsl(var(--border))',
  
  // Typography
  fontFamily: 'inherit',
  fontSize: '0.875rem', // text-sm
  fontWeight: '500', // font-medium
  lineHeight: '1.25rem',
  
  // Spacing
  padding: '0.5rem 1rem', // px-4 py-2
  margin: '0',
  gap: '0.25rem', // gap-1
  
  // Effects
  borderRadius: '0.375rem', // rounded-md
  shadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  transition: 'all 0.2s ease-in-out',
  
  // States
  hover: {
    backgroundColor: 'hsl(var(--primary) / 0.1)',
    textColor: 'hsl(var(--primary))',
    transform: 'translateY(-1px)'
  },
  
  active: {
    backgroundColor: 'hsl(var(--primary))',
    textColor: 'hsl(var(--primary-foreground))',
    borderColor: 'hsl(var(--primary))'
  },
  
  // Variants
  variants: {
    horizontal: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '0.25rem'
    },
    vertical: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem'
    },
    mobile: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '1rem'
    },
    dropdown: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.125rem',
      padding: '0.5rem'
    }
  }
};

interface NavigationThemeProviderProps {
  children: React.ReactNode;
  customTheme?: Partial<NavigationTheme>;
}

export const NavigationThemeProvider: React.FC<NavigationThemeProviderProps> = ({
  children,
  customTheme = {}
}) => {
  const { language } = useLanguage();
  const [themeOverrides, setThemeOverrides] = React.useState<Partial<NavigationTheme>>(customTheme);

  // Merge default theme with custom overrides
  const theme = useMemo(() => {
    return {
      ...DEFAULT_THEME,
      ...themeOverrides,
      // Adjust font family based on language
      fontFamily: language === 'ar' ? 'font-arabic-body' : 'font-english-body'
    };
  }, [themeOverrides, language]);

  const updateTheme = (updates: Partial<NavigationTheme>) => {
    setThemeOverrides(prev => ({
      ...prev,
      ...updates
    }));
  };

  const resetTheme = () => {
    setThemeOverrides(customTheme);
  };

  const getThemeClasses = (variant: string, state?: string): string => {
  const classes: string[] = [];
    
    // Base classes
    classes.push('transition-all duration-200');
    classes.push(`text-[${theme.fontSize}]`);
    classes.push(`font-[${theme.fontWeight}]`);
    classes.push(`rounded-[${theme.borderRadius}]`);
    
    // Variant classes
    const variantConfig = theme.variants[variant as keyof typeof theme.variants];
    if (variantConfig) {
      Object.entries(variantConfig).forEach(([key, value]) => {
        const cssProperty = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        if (typeof value === 'string') {
          classes.push(`[${cssProperty}:${value}]`);
        }
      });
    }
    
    // State classes
    if (state === 'hover') {
      classes.push('hover:bg-primary/10');
      classes.push('hover:text-primary');
      classes.push('hover:-translate-y-0.5');
    } else if (state === 'active') {
      classes.push('bg-primary');
      classes.push('text-primary-foreground');
      classes.push('border-primary');
    }
    
    // Language-specific classes
    classes.push(theme.fontFamily);
    
    return classes.join(' ');
  };

  const value = {
    theme,
    updateTheme,
    resetTheme,
    getThemeClasses
  };

  return (
    <NavigationThemeContext.Provider value={value}>
      {children}
    </NavigationThemeContext.Provider>
  );
};